import { Big } from "big.js";
// type-only: Agreements.ts imports DisbursementType from this module — the
// erased import keeps the cycle out of the emitted JS.
import type { PartyAgreements } from "./Agreements.ts";
import type { Assessment, Payment } from "./judgment.ts";

export enum Relation {
  And,
  Or,
  Xor,
}

/**
 * Base case types
 */
export interface Party {
  readonly id: string;
  name: string;
  /** The rate of the VAT the party pays on its costs, absent when it reclaims its VAT (see vat.ts). */
  vatRate?: number;
}

export interface Issue {
  readonly id: string;
  title: string;
}

export interface LiabilityIssue extends Issue {
  defendants?: string[];
  subIssues?: LiabilityIssue[];
  relation?: Relation;
  /** One decision for every defendant: see LiabilityScenarios.Issue. */
  generic?: boolean;
  setOff?: boolean;
}

export interface Claim extends LiabilityIssue {
  claimant: string;
}

export interface QuantumIssue extends Issue {
  claimant: string;
  liabilities: string[];
  value: number;
}

export interface Case {
  id: string;
  title: string;
  claimType?: string;
  parties?: Party[];
  liabilityIssues?: Claim[];
  quantumIssues?: QuantumIssue[];
  /**
   * Per-side fee agreements, carried verbatim from the client case. The
   * analytics input is NOT agreements-blind: cost bearer attribution
   * (ledger/attribution.ts) and recovery gating derive from these.
   */
  agreements?: PartyAgreements[];
}

/**
 * Scenario types
 */
export interface Cost {
  costId: string;
  type: CostType;
  incurred?: boolean;
  amount: number;
  /**
   * The litigation side this cost belongs to (case-party id). NOT necessarily
   * who pays for the work — under a CFA the law firm or barrister may bear it
   * (attribution happens in the ledger generation, not here).
   */
  partyId: string;
  stage: Stage;
  date?: Date;
  /**
   * What of the amount is incurred towards single opponents; the rest of it
   * is generic, owed by every opponent the party wins against. Absent, all of
   * it is generic.
   */
  towards?: { [opponentId: string]: number };
  /**
   * Set when this cost row is auto-derived from a settlement's
   * `costs.settlement.{offeror,offeree}` (lawyer time spent drafting /
   * negotiating that settlement, billed whether or not the settlement was
   * accepted). The dialog uses this to render those rows as detail sub-rows
   * under "Law firm fees", attributing them to the originating settlement.
   */
  settlementId?: string;
}

/** What of a cost is generic: its amount less its parts towards single opponents. */
export function genericOf({
  amount,
  towards,
}: Pick<Cost, "amount" | "towards">): Big {
  const rest = Object.values(towards ?? {}).reduce(
    (left, part) => left.minus(part),
    new Big(amount),
  );
  return rest.gt(0) ? rest : new Big(0);
}

export type CostType =
  | "lawFirmFee"
  | "courtFee"
  | "barristerFee"
  | "expertFee"
  | "otherDisbursement";

export function costEntries<V>(o: { [type in CostType]?: V }): [CostType, V][] {
  return Object.entries(o) as [CostType, V][];
}

export function mapCosts<V, M>(
  o: { [type in CostType]?: V },
  callbackFn: (value: V) => M,
): { [type in CostType]?: M } {
  const result: { [type in CostType]?: M } = {};
  for (const [type, value] of costEntries(o)) result[type] = callbackFn(value);
  return result;
}

/** Adds `costs` to `into` type by type, each amount scaled by `weight`. */
export function addCosts(
  into: { [type in CostType]?: number },
  costs: { [type in CostType]?: number },
  weight = 1,
): void {
  for (const [type, amount] of costEntries(costs))
    into[type] = (into[type] ?? 0) + (amount ?? 0) * weight;
}

export type DisbursementType = Exclude<CostType, "lawFirmFee">;

export interface AssetLimit {
  // who does this limit apply to
  partyId: string;
  // monetary value with probability of this asset to have the value at time of judgement
  amount: LikelyValue;
  // comment
  note?: string;
}

export interface QuantumAmount {
  // what quantum issue?
  quantumId: string;
  // refines when this amount applies
  when?: IssueScenario;
  // what is the amount of the quantum for the liability issue
  amount: number;
  // amount may differ per defendant if the issue has multiple liable defendants (e.g. a generic issue)
  perDefendant?: { [defendantId: string]: number };
}

export interface IssueConfiguration {
  liabilityId: string;
  winChance: number;
}

export interface IssueScenario {
  issuesLost?: string[];
  issuesWon?: string[];
}

export interface LiabilityScenario extends IssueScenario {
  readonly probability: Big;
}

export interface Share {
  partyId: string;
  percentage?: number; // 0.0-1.0
}

export interface Recovery {
  recipient: string;
  type: CostType;
  percentage: number;
  // payer id mapped to % share of payer in this recovery's payment (applied to amount remaining after recovery percentage has been applied!)
  payers?: Share[];
  when?: IssueScenario;
  description?: string;
}

export enum Stage {
  PreAction,
  IssueStatementsOfCase,
  CMC,
  Disclosure,
  WitnessStatements,
  ExpertReports,
  PTR,
  TrialPreparation,
  Trial,
  Appeals,
  Enforcement,
}

/**
 * What settlements covered of a cost, as its party pays it (see
 * settlementCaps): it is not recovered again at trial.
 */
export interface CostCoverage {
  // of its share towards each opponent
  towards: { [opponentId: string]: number };
  // of its generic share, owed by every opponent the party wins against
  generic: number;
}

export interface CaseVariant {
  stages: Stage[];
  costs?: Cost[];
  // what settlements covered of the costs, by costId
  costsCovered?: { [costId: string]: CostCoverage };
  costRecoveries?: Recovery[];
  // what the court assessed of what a party recovers, by recipient
  assessments?: { [recipientId: string]: Assessment };
  // what was actually paid, once the case has ended
  actualPayments?: Payment[];
  assetLimits?: AssetLimit[];
  liabilityWinChances: IssueConfiguration[];
  quantumAmounts?: QuantumAmount[];
}

export function equalShares(share1: Share, share2: Share) {
  if (share1 === share2) {
    return true;
  }
  if (share1 === undefined || share2 === undefined) {
    return false;
  }
  return (
    share1.partyId === share2.partyId && share1.percentage === share2.percentage
  );
}

/**
 * Enables the definition of a value as an aggregate of multiple values with a probability.
 * - A flat value is expressed as a LikelyValue with probability of 1 (which is the default, so the field can be left undefined)
 * - Use 'or' to build a chain of several likely values.
 */
export interface LikelyValue {
  readonly value: number;

  /**
   * Probability expressed as decimal [0..1]. 1 (100%) is assumed if undefined.
   */
  readonly probability?: number;

  /**
   * Possible other value.
   */
  readonly or?: LikelyValue;
}
